import {
  Component,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  ElementRef,
  ViewChild,
  HostBinding,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Graph } from '@antv/x6';

/**
 * @class DataModelsDiagramComponent
 * @description Dumb component responsible for rendering an interactive architecture diagram of data models and their relations using @antv/x6.
 */
@Component({
  selector: 'rapider-data-models-diagram',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="w-full h-full relative overflow-hidden bg-body-background shadow-inner"
    >
      <div #graphContainer class="w-full h-full absolute inset-0"></div>
    </div>
  `,
})
export class DataModelsDiagramComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input({ required: true }) graphData!: { nodes: any[]; edges: any[] };

  /** Control the height of the wrapper explicitly for the X6 canvas calculations */
  @Input() heightPx: number = 350;

  // Use HostBinding to strictly bind the dynamic height property so it is always respected by layout engines
  @HostBinding('style.height.px') get hostHeight() {
    return this.heightPx;
  }
  @HostBinding('style.min-height.px') get hostMinHeight() {
    return this.heightPx;
  }
  @HostBinding('class') hostClass =
    'block w-full relative border-y border-primary-border';

  @ViewChild('graphContainer', { static: false })
  private graphContainer!: ElementRef;

  private graphInstance: Graph | null = null;

  ngAfterViewInit(): void {
    this.initGraph();
    this.renderGraph();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['graphData'] && !changes['graphData'].isFirstChange()) {
      this.renderGraph();
    }
  }

  ngOnDestroy(): void {
    if (this.graphInstance) {
      this.graphInstance.dispose();
    }
  }

  /**
   * Initializes the @antv/x6 Graph canvas instance on the ViewChild container.
   */
  private initGraph(): void {
    if (!this.graphContainer) return;

    this.graphInstance = new Graph({
      container: this.graphContainer.nativeElement,
      autoResize: true,
      grid: {
        size: 10,
        visible: true,
        type: 'dot',
        args: { color: 'var(--color-secondary-border)', thickness: 1 },
      },
      panning: { enabled: true },
      mousewheel: { enabled: true, modifiers: ['ctrl', 'meta'] },
    });
  }

  /**
   * Loads the nodes and edges into the graph.
   * Employs a tiny timeout to ensure Angular has fully dimensioned the container before centering content.
   */
  private renderGraph(): void {
    if (!this.graphInstance || !this.graphData) return;

    this.graphInstance.fromJSON({
      nodes: this.graphData.nodes,
      edges: this.graphData.edges,
    });
    setTimeout(() => {
      this.graphInstance?.centerContent();
    }, 50);
  }
}
